import { useState } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Flex,
  Grid,
  List,
  Progress,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { FonteDeLegislacao, RelatorioDeReimportacao } from '@titularis/api-client';
import { api } from '../api';
import { formatarData } from '../dominio';

/**
 * O acervo de legislação (D-25, D-29).
 *
 * **Não é um formulário de cadastro, e a diferença é a decisão inteira.** Quais
 * fontes existem é decidido pelo catálogo versionado em `catalogo.ts`, revisável
 * em PR e reproduzível do zero; digitar URLs numa tela produziria um acervo que
 * ninguém sabe reconstruir. O que a tela faz é mostrar **o estado**: o que já
 * entrou, o que falta, e onde a contagem divergiu do que foi medido na fonte.
 *
 * **Manutenção, sim; cadastro, não.** Reimportar uma fonte é ação da tela, e é a
 * que paga: a lei muda, e o que a reimportação devolve são as questões
 * publicadas que citam dispositivo alterado - o ganho que o D-25 usa como
 * argumento para ter a lei em casa. O que a tela não faz é criar fonte nova,
 * porque a reimportação repete o que o catálogo descreve (com o recorte e o piso
 * dele) e a rota crua não saberia repetir.
 *
 * **No celular a tabela vira lista.** São sete colunas, e a última é o botão de
 * recarregar - a única ação da tela. Rolar de lado para alcançá-la seria pedir
 * que se descubra que ela existe.
 */

/** Quanto a contagem pode fugir do medido antes de merecer um olhar. */
const TOLERANCIA = 0.02;

/**
 * O que aconteceu com esta fonte, em uma palavra.
 *
 * Contagem sozinha não diz: 1.106 de 1.106 e 0 de 1.106 são o mesmo campo, e um
 * é o desenho funcionando enquanto o outro é trabalho por fazer.
 */
function situacaoDe(fonte: FonteDeLegislacao) {
  if (fonte.vigentes === 0) return { cor: 'default', texto: 'não carregada' } as const;
  if (fonte.medido === null) return { cor: 'blue', texto: 'fora do catálogo' } as const;

  const desvio = Math.abs(fonte.vigentes - fonte.medido) / fonte.medido;
  if (desvio <= TOLERANCIA) return { cor: 'green', texto: 'carregada' } as const;
  return { cor: 'orange', texto: 'contagem diverge' } as const;
}

/** O que a reimportação achou, que é o motivo de reimportar. */
function RelatorioDaReimportacao({ relatorio }: { relatorio: RelatorioDeReimportacao }) {
  return (
    <Space orientation="vertical" size="small" style={{ width: '100%' }}>
      {relatorio.questoesAfetadas.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          title={`${relatorio.questoesAfetadas.length} questão(ões) publicada(s) citam dispositivo que mudou`}
          description={
            'É o que a reimportação existe para achar: o texto da lei mudou e o comentário ' +
            'publicado ainda cita a redação anterior. ' +
            relatorio.questoesAfetadas
              .map((q) => `questão ${q.position ?? '?'} (${q.enderecos.join(', ')})`)
              .join('; ')
          }
        />
      ) : null}

      {/* Três colunas em 390px dariam ~55px por valor: uma por linha no celular. */}
      <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} bordered>
        <Descriptions.Item label="Vigentes">{relatorio.encontrados}</Descriptions.Item>
        <Descriptions.Item label="Antes">{relatorio.anteriores}</Descriptions.Item>
        <Descriptions.Item label="Redações anteriores">
          {relatorio.redacoesAnteriores}
        </Descriptions.Item>
        <Descriptions.Item label="Novos">{relatorio.novos.length}</Descriptions.Item>
        <Descriptions.Item label="Alterados">{relatorio.alterados.length}</Descriptions.Item>
        <Descriptions.Item label="Sumidos">
          <Tooltip title="Numa lei viva isto é raro, e costuma ser sinal de parser quebrado e não de dispositivo revogado.">
            {relatorio.sumidos.length}
          </Tooltip>
        </Descriptions.Item>
      </Descriptions>

      {relatorio.colisoes.length > 0 ? (
        <Typography.Text type="secondary">
          {relatorio.colisoes.length} endereço(s) repetidos sem o documento explicar por quê,
          gravada a primeira ocorrência: {relatorio.colisoes.slice(0, 6).join(', ')}
          {relatorio.colisoes.length > 6 ? '…' : ''}
        </Typography.Text>
      ) : null}
    </Space>
  );
}

export function LegislacaoDoAcervo() {
  const compacto = !Grid.useBreakpoint().md;
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [ultimo, setUltimo] = useState<RelatorioDeReimportacao | null>(null);

  const fontes = useQuery({
    queryKey: ['corpus', 'legislacoes'],
    queryFn: () => api.listCorpusLegislations(),
  });

  const reimportar = useMutation({
    mutationFn: (citation: string) => api.reimportCorpusLegislation(citation),
    onSuccess: (relatorio) => {
      setUltimo(relatorio);
      message.success(`${relatorio.citation}: ${relatorio.encontrados} dispositivos vigentes.`);
      void queryClient.invalidateQueries({ queryKey: ['corpus'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const lista = fontes.data ?? [];
  const carregadas = lista.filter((f) => f.vigentes > 0);
  const faltando = lista.filter((f) => f.vigentes === 0);
  const divergentes = carregadas.filter((f) => situacaoDe(f).texto === 'contagem diverge');
  const vigentes = carregadas.reduce((soma, f) => soma + f.vigentes, 0);

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        A lei entra por importação estruturada, dispositivo a dispositivo, e nunca como PDF:{' '}
        <Typography.Text code>art. 52, § 1º, da Lei 6.015/1973</Typography.Text> é uma chave, não uma
        busca. <strong>Quais</strong> fontes existem é decidido no catálogo versionado, em código
        revisável; daqui você acompanha o estado de cada uma e a recarrega quando a lei mudar.
      </Typography.Text>

      {divergentes.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          title="Alguma fonte devolveu uma contagem diferente da medida"
          description={
            `${divergentes.map((f) => f.citation).join(', ')}: a última importação não bateu com o ` +
            'que o catálogo mediu na fonte. Costuma ser lei alterada desde a medição, e às vezes é a ' +
            'página que mudou de formato. Vale conferir antes de usar o material.'
          }
        />
      ) : null}

      {faltando.length > 0 ? (
        <Alert
          type="info"
          showIcon
          title={`${faltando.length} fonte(s) do catálogo ainda não foram carregadas`}
          description={
            `${faltando.map((f) => f.citation).join(', ')}. Use "Carregar" na linha de cada uma, ` +
            'ou `pnpm acervo:legislacao` para todas de uma vez - os dois caminhos passam pelas ' +
            'mesmas travas: o piso de dispositivos e a conferência da identidade que a fonte declara.'
          }
        />
      ) : null}

      {ultimo ? (
        <Card
          title={`Última reimportação: ${ultimo.citation}`}
          size={compacto ? 'small' : undefined}
        >
          <RelatorioDaReimportacao relatorio={ultimo} />
        </Card>
      ) : null}

      <Card size={compacto ? 'small' : undefined}>
        <Space size="large" wrap style={{ marginBottom: 16 }}>
          <Typography.Text>
            <strong>{vigentes.toLocaleString('pt-BR')}</strong> dispositivos vigentes
          </Typography.Text>
          <Typography.Text type="secondary">
            {carregadas.length} de {lista.length} fontes carregadas
          </Typography.Text>
        </Space>

        {/*
          O botão de recarregar sai da última coluna e vira a última linha do
          cartão. Ele é a única ação desta tela, e numa tabela de sete colunas
          seria a primeira coisa a desaparecer do campo de visão.
        */}
        {compacto ? (
          <List<FonteDeLegislacao>
            loading={fontes.isLoading}
            dataSource={lista}
            renderItem={(fonte) => {
              const situacao = situacaoDe(fonte);
              return (
                <List.Item style={{ paddingInline: 0, alignItems: 'flex-start' }}>
                  <Flex vertical gap={6} style={{ width: '100%', minWidth: 0 }}>
                    <Flex align="center" gap={8} wrap>
                      <Typography.Text code>{fonte.citation}</Typography.Text>
                      <Tag color={situacao.cor} style={{ marginInlineEnd: 0 }}>
                        {situacao.texto}
                      </Tag>
                    </Flex>

                    <Typography.Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                      {fonte.title}
                    </Typography.Text>

                    <Flex align="center" gap={8} wrap>
                      <Typography.Text style={{ fontSize: 13 }}>
                        {fonte.vigentes.toLocaleString('pt-BR')}
                        {fonte.medido === null
                          ? ''
                          : ` / ${fonte.medido.toLocaleString('pt-BR')}`}{' '}
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                          vigentes
                        </Typography.Text>
                      </Typography.Text>
                      {fonte.redacoesAnteriores > 0 ? (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          · {fonte.redacoesAnteriores.toLocaleString('pt-BR')} redação(ões)
                          anterior(es)
                        </Typography.Text>
                      ) : null}
                    </Flex>

                    {fonte.medido === null ? null : (
                      <Progress
                        percent={Math.min(
                          100,
                          Math.round((fonte.vigentes / fonte.medido) * 100),
                        )}
                        showInfo={false}
                        size="small"
                        status={fonte.vigentes === 0 ? 'normal' : undefined}
                      />
                    )}

                    <Flex align="center" gap={12} wrap>
                      {fonte.noCatalogo ? (
                        <Button
                          size="small"
                          icon={<ReloadOutlined />}
                          loading={reimportar.isPending && reimportar.variables === fonte.citation}
                          disabled={reimportar.isPending}
                          onClick={() => reimportar.mutate(fonte.citation)}
                        >
                          {fonte.vigentes === 0 ? 'Carregar' : 'Reimportar'}
                        </Button>
                      ) : (
                        <Tooltip title="Está no banco e não no catálogo versionado: entrou pela rota, e a carga em lote não a recarrega.">
                          <Typography.Text type="warning" style={{ fontSize: 12 }}>
                            fora do catálogo
                          </Typography.Text>
                        </Tooltip>
                      )}
                      <Typography.Link
                        href={fonte.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 12 }}
                      >
                        {new URL(fonte.sourceUrl).hostname}
                      </Typography.Link>
                      {fonte.importedAt ? (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {formatarData(fonte.importedAt)}
                        </Typography.Text>
                      ) : null}
                    </Flex>
                  </Flex>
                </List.Item>
              );
            }}
          />
        ) : (
        <Table<FonteDeLegislacao>
          rowKey="citation"
          size="small"
          loading={fontes.isLoading}
          dataSource={lista}
          pagination={false}
          columns={[
            {
              title: 'Como se cita',
              dataIndex: 'citation',
              width: 210,
              render: (citacao: string, fonte) => (
                <Space orientation="vertical" size={0}>
                  <Typography.Text code>{citacao}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {fonte.title}
                  </Typography.Text>
                </Space>
              ),
            },
            {
              title: 'Situação',
              width: 150,
              render: (_, fonte) => {
                const situacao = situacaoDe(fonte);
                return <Tag color={situacao.cor}>{situacao.texto}</Tag>;
              },
            },
            {
              title: 'Vigentes',
              dataIndex: 'vigentes',
              width: 150,
              align: 'right',
              render: (quantos: number, fonte) =>
                fonte.medido === null ? (
                  quantos.toLocaleString('pt-BR')
                ) : (
                  <Tooltip
                    title={`O catálogo mediu ${fonte.medido.toLocaleString('pt-BR')} na fonte, e recusa a importação abaixo de ${fonte.minimoEsperado?.toLocaleString('pt-BR')}.`}
                  >
                    {quantos.toLocaleString('pt-BR')}{' '}
                    <Typography.Text type="secondary">
                      / {fonte.medido.toLocaleString('pt-BR')}
                    </Typography.Text>
                    <Progress
                      percent={Math.min(100, Math.round((quantos / fonte.medido) * 100))}
                      showInfo={false}
                      size="small"
                      status={quantos === 0 ? 'normal' : undefined}
                    />
                  </Tooltip>
                ),
            },
            {
              title: 'Redações anteriores',
              dataIndex: 'redacoesAnteriores',
              width: 160,
              align: 'right',
              render: (quantas: number) => (
                <Tooltip title="Redações superadas, guardadas e fora da consulta. É o que permite comentar hoje uma questão de anos atrás com o texto da época.">
                  {quantas > 0 ? quantas.toLocaleString('pt-BR') : '—'}
                </Tooltip>
              ),
            },
            {
              title: 'Importada em',
              dataIndex: 'importedAt',
              width: 160,
              render: (quando: string | null) =>
                quando ? formatarData(quando) : <Typography.Text type="secondary">—</Typography.Text>,
            },
            {
              title: '',
              width: 130,
              render: (_, fonte) =>
                fonte.noCatalogo ? (
                  <Tooltip title="Busca a fonte de novo e mostra o que mudou desde a última vez, com as questões publicadas que citam dispositivo alterado.">
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      loading={reimportar.isPending && reimportar.variables === fonte.citation}
                      disabled={reimportar.isPending}
                      onClick={() => reimportar.mutate(fonte.citation)}
                    >
                      {fonte.vigentes === 0 ? 'Carregar' : 'Reimportar'}
                    </Button>
                  </Tooltip>
                ) : null,
            },
            {
              title: 'Fonte',
              dataIndex: 'sourceUrl',
              render: (url: string, fonte) => (
                <Space orientation="vertical" size={0}>
                  <Typography.Link href={url} target="_blank" rel="noreferrer">
                    {new URL(url).hostname}
                  </Typography.Link>
                  {fonte.noCatalogo ? null : (
                    <Tooltip title="Está no banco e não no catálogo versionado: entrou pela rota, e a carga em lote não a recarrega.">
                      <Typography.Text type="warning" style={{ fontSize: 12 }}>
                        fora do catálogo
                      </Typography.Text>
                    </Tooltip>
                  )}
                </Space>
              ),
            },
          ]}
        />
        )}
      </Card>
    </Space>
  );
}
